import {
  MUSIC24_PER_ARTIST_LIMITS,
  MUSIC24_TIER_RANK,
  MUSIC24_TRIAL_LEVEL_TIER,
  MUSIC24_TRIAL_OUTREACH,
  Music24Capabilities,
  Music24PlanTier,
  music24PlanCapabilities,
} from 'fym-shared/src/music24/music24Plans';
import { type MonthlyAddOnQuantities, withMonthlyAddOns } from 'fym-shared/src/music24/monthlyAddOns';
import { LicenseModelProductType } from './consts';

export * from 'fym-shared/src/music24/music24Plans';

export const MUSIC24_PRODUCT_TYPES: LicenseModelProductType[] = [
  LicenseModelProductType.Music24Basic,
  LicenseModelProductType.Music24ArtistPro,
  LicenseModelProductType.Music24Plug,
  LicenseModelProductType.Music24Premium,
  LicenseModelProductType.Music24Pro,
  LicenseModelProductType.Music24Trial,
];

export function music24TierForProductType(productType: LicenseModelProductType | null): Music24PlanTier | null {
  switch (productType) {
    case LicenseModelProductType.Music24Basic:
      return 'artist';
    case LicenseModelProductType.Music24ArtistPro:
      return 'pro';
    case LicenseModelProductType.Music24Plug:
      return 'plug';
    case LicenseModelProductType.Music24Premium:
      return 'manager';
    case LicenseModelProductType.Music24Pro:
      return 'label';
    case LicenseModelProductType.Music24Trial:
      return 'trial';
    case null:
    case LicenseModelProductType.Unknown:
    case LicenseModelProductType.OldSingleApp:
    case LicenseModelProductType.OldBundle:
    case LicenseModelProductType.SingleApp:
    case LicenseModelProductType.SubscriptionYearly:
    case LicenseModelProductType.SubscriptionQuarterly:
    case LicenseModelProductType.SubscriptionMonthly:
    case LicenseModelProductType.SubscriptionLifeTime:
    case LicenseModelProductType.SubscriptionFromReferral:
    case LicenseModelProductType.SmartLinksInfluencer:
    case LicenseModelProductType.SmartLinksArtist:
    case LicenseModelProductType.SmartLinksTrial:
    case LicenseModelProductType.SmartLinksRookie:
    case LicenseModelProductType.Music24NoLicense:
      // The pre-Stripe cardless trial used this type with the Trial vendor. It is not a tier.
      return null;
  }
}

/**
 * The highest tier among a licence's valid items; `free` when none is a Music24 plan. This is the
 * plan name. A paid plan wins over the trial, so the name is the plan the person pays for. For the
 * limits, use `resolveMusic24Capabilities`.
 */
export function resolveMusic24Tier(validProductTypes: (LicenseModelProductType | null)[]): Music24PlanTier {
  let best: Music24PlanTier = 'free';
  for (const productType of validProductTypes) {
    const tier = music24TierForProductType(productType);
    if (tier !== null && MUSIC24_TIER_RANK[tier] > MUSIC24_TIER_RANK[best]) {
      best = tier;
    }
  }
  return best;
}

/**
 * The capabilities of a licence's valid items.
 *
 * While the trial item is valid, the licence gets the trial limits (the `MUSIC24_TRIAL_LEVEL_TIER`
 * plan) when its paid plan is below the trial level. Thus a person who buys Artist during the
 * trial keeps the trial limits until the trial ends, and then gets the Artist limits. The video
 * credits are the exception: the trial has fewer than Artist could have, so the higher number
 * wins. A paying person never gets previews: the watermark, the preview flag and the render lane
 * stay those of the paid plan, and so do the branding removal and the custom domain (the trial has
 * neither, `MUSIC24_TRIAL_PRO_EXCEPTIONS`). The Auto campaign posts a day take the higher value. The Outreach numbers stay those of the paid plan too: the trial
 * has its own small numbers (`MUSIC24_TRIAL_OUTREACH`), and a plan that a person pays for keeps its own. `plan` is the plan name from `resolveMusic24Tier`, so the apps show
 * the plan that the person pays for.
 *
 * `options.extraArtists` is the number of paid extra artists (Plus, Pro, Max and Ultra; owner
 * decision 2026-10-23). They add to `artistsLimit` up to `extraArtistLimit`, and each one gets a full
 * copy of the per-artist limits (`MUSIC24_PER_ARTIST_LIMITS`, `withExtraArtists`).
 *
 * `options.monthlyAddOns` are the monthly add-ons of a web subscriber (spec 2026-10-19). They add to
 * `scheduledPostsPerMonth` and `videoCreditsPerMonth` of a paid plan, after the trial rule and the
 * extra artists (`withMonthlyAddOns`). Free and the trial get nothing.
 */
export function resolveMusic24Capabilities(
  validProductTypes: (LicenseModelProductType | null)[],
  options?: { extraArtists?: number; monthlyAddOns?: Partial<MonthlyAddOnQuantities> }
): Music24Capabilities {
  return withMonthlyAddOns(resolvePlanCapabilities(validProductTypes, options?.extraArtists), options?.monthlyAddOns);
}

/** The capabilities of the plan, with the trial rule and the extra artists, before the add-ons. */
function resolvePlanCapabilities(
  validProductTypes: (LicenseModelProductType | null)[],
  extraArtists: number | undefined
): Music24Capabilities {
  const tier = resolveMusic24Tier(validProductTypes);
  const hasValidTrial = validProductTypes.includes(LicenseModelProductType.Music24Trial);
  if (hasValidTrial && tier !== 'trial' && MUSIC24_TIER_RANK[tier] < MUSIC24_TIER_RANK[MUSIC24_TRIAL_LEVEL_TIER]) {
    const { trial } = music24PlanCapabilities;
    const own = music24PlanCapabilities[tier];
    // null is no limit, so it wins over any number.
    const videoCreditsPerMonth =
      trial.videoCreditsPerMonth === null || own.videoCreditsPerMonth === null
        ? null
        : Math.max(trial.videoCreditsPerMonth, own.videoCreditsPerMonth);
    const withTrial: Music24Capabilities = {
      ...trial,
      ...ownOutreachNumbers(own),
      videoCreditsPerMonth,
      plan: tier,
      clipWatermark: own.clipWatermark,
      previewRenders: own.previewRenders,
      renderLane: own.renderLane,
      // The fields the trial does not get (`MUSIC24_TRIAL_PRO_EXCEPTIONS`) stay those of the paid plan.
      canRemoveBranding: own.canRemoveBranding,
      canUseCustomDomain: own.canUseCustomDomain,
      canUseX: own.canUseX,
      xPostsPerMonth: own.xPostsPerMonth,
      // The trial has fewer Auto campaign posts a day than a paid plan: the higher number wins.
      reelsPlanMaxPostsPerDay: Math.max(trial.reelsPlanMaxPostsPerDay, own.reelsPlanMaxPostsPerDay) as 1 | 2 | 3,
      // The artists stay those of the paid plan: the trial sells no extra artist, the paid plan does.
      artistsLimit: own.artistsLimit,
      includedArtists: own.includedArtists,
      extraArtistLimit: own.extraArtistLimit,
      videoCreditsPerArtist: own.videoCreditsPerArtist,
    };
    // Each paid extra artist gets a full copy of the limits that the person has now (the trial limits).
    return withExtraArtists(withTrial, extraArtists ?? 0);
  }
  return withExtraArtists(music24PlanCapabilities[tier], extraArtists ?? 0);
}

/** The Outreach fields that the trial overrides (`MUSIC24_TRIAL_OUTREACH`), with the values of `own`. */
function ownOutreachNumbers(own: Music24Capabilities): Pick<Music24Capabilities, keyof typeof MUSIC24_TRIAL_OUTREACH> {
  return {
    outreachRevealsPerMonth: own.outreachRevealsPerMonth,
    outreachSendsPerMonth: own.outreachSendsPerMonth,
    outreachMailboxes: own.outreachMailboxes,
    outreachPitchesPerDay: own.outreachPitchesPerDay,
    canUseOutreachAutopilot: own.canUseOutreachAutopilot,
    canExportOutreachContacts: own.canExportOutreachContacts,
  };
}

/**
 * Adds the paid extra artists to a plan, up to its cap (owner decision 2026-10-23, D2). Every plan
 * includes 1 artist, and the plan table gives the limits of that one artist. With N artists, each
 * limit of `MUSIC24_PER_ARTIST_LIMITS` is the plan value times N (null stays null: no limit). The
 * seats and the other limits stay those of the tier. Pure. The admin list uses it with the plan of
 * the tier SQL.
 */
export function withExtraArtists(base: Music24Capabilities, requested: number): Music24Capabilities {
  const extra = Math.max(0, Math.min(Math.floor(requested), base.extraArtistLimit ?? Number.POSITIVE_INFINITY));
  if (!Number.isFinite(extra) || extra <= 0) {
    return base;
  }
  const artists = base.includedArtists + extra;
  const result: Music24Capabilities = { ...base, artistsLimit: artists };
  for (const key of MUSIC24_PER_ARTIST_LIMITS) {
    const value = base[key];
    result[key] = value === null ? null : Math.round((value / Math.max(1, base.includedArtists)) * artists);
  }
  return result;
}

/**
 * The end of the trial limits for a paid plan bought during the trial, or null.
 *
 * `resolveMusic24Capabilities` gives a plan below `MUSIC24_TRIAL_LEVEL_TIER` (Artist) the trial
 * limits while the `Music24Trial` item is valid. This returns the last end of a valid trial item
 * in that case, so the apps can say "Pro limits until <date>, then Artist". It is null for
 * Free, for the trial alone and for a plan at or above the trial level, because the limits do not
 * drop then.
 */
export function music24TrialLimitsUntil(
  validItems: { productType: LicenseModelProductType | null; subscriptionExpiresAt: Date | null }[]
): number | null {
  const tier = resolveMusic24Tier(validItems.map((item) => item.productType));
  if (tier === 'free' || tier === 'trial' || MUSIC24_TIER_RANK[tier] >= MUSIC24_TIER_RANK[MUSIC24_TRIAL_LEVEL_TIER]) {
    return null;
  }
  const trialEnds = validItems
    .filter((item) => item.productType === LicenseModelProductType.Music24Trial)
    .map((item) => item.subscriptionExpiresAt?.getTime() ?? null)
    .filter((end): end is number => end !== null);
  return trialEnds.length > 0 ? Math.max(...trialEnds) : null;
}
